import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { Button } from 'components/ui/PermissionActions';
import Stack from '@mui/material/Stack';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-reload on dynamic import / chunk load failure (server restart / HMR cache mismatch)
    const errorMsg = error?.message || '';
    const isChunkOrFetchError =
      errorMsg.includes('Failed to fetch dynamically imported module') ||
      errorMsg.includes('Importing a module script failed') ||
      error?.name === 'ChunkLoadError';

    if (isChunkOrFetchError) {
      const lastReload = parseInt(sessionStorage.getItem('last_chunk_reload_time') || '0', 10);
      const now = Date.now();

      // Only auto-reload if we haven't reloaded in the last 10 seconds (prevents loops)
      if (now - lastReload > 10000) {
        sessionStorage.setItem('last_chunk_reload_time', String(now));
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    sessionStorage.removeItem('last_chunk_reload_time');
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const isDynamicImportError =
        this.state.error?.message?.includes('Failed to fetch dynamically imported module') ||
        this.state.error?.name === 'ChunkLoadError';

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            p: 3
          }}
        >
          <Card
            sx={{
              maxWidth: 520,
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              border: '1px solid #E2E8F0'
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#FEF3C7',
                color: '#D97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                mx: 'auto',
                mb: 2.5
              }}
            >
              <WarningOutlined />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
              {isDynamicImportError ? 'Page Version Updated' : 'Something went wrong'}
            </Typography>

            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              {isDynamicImportError
                ? 'The application or dev server was updated while you were browsing. Please reload the page to load the latest module.'
                : (this.state.error?.message || 'An unexpected error occurred while loading this view.')}
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<ReloadOutlined />}
                onClick={this.handleReload}
                sx={{
                  bgcolor: '#2563EB',
                  '&:hover': { bgcolor: '#1D4ED8' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5
                }}
              >
                Reload Page
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeOutlined />}
                onClick={this.handleGoHome}
                sx={{
                  borderColor: '#CBD5E1',
                  color: '#475569',
                  '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Dashboard
              </Button>
            </Stack>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node
};

export function RouteErrorBoundary() {
  const error = useRouteError();
  const errorMessage = error instanceof Error ? error.message : '';
  const isDynamicImportError =
    error?.name === 'ChunkLoadError' ||
    /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i.test(
      errorMessage
    );
  const title = isDynamicImportError ? 'Page Version Updated' : 'Unable to load this page';
  const description = isDynamicImportError
    ? 'A new version of this screen is available or the connection was interrupted. Reload the page to load the latest module.'
    : isRouteErrorResponse(error)
      ? `${error.status} ${error.statusText || 'Error'}${error.data?.message ? `: ${error.data.message}` : ''}`
      : errorMessage || 'An unexpected error occurred while loading this page.';

  const handleReload = () => {
    sessionStorage.removeItem('last_chunk_reload_time');
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}
    >
      <Card
        sx={{
          maxWidth: 520,
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
          border: '1px solid #E2E8F0'
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: '#FEF3C7',
            color: '#D97706',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            mx: 'auto',
            mb: 2.5
          }}
        >
          <WarningOutlined />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
          {description}
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<ReloadOutlined />}
            onClick={handleReload}
            sx={{
              bgcolor: '#2563EB',
              '&:hover': { bgcolor: '#1D4ED8' },
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5
            }}
          >
            Reload Page
          </Button>
          <Button
            variant="outlined"
            startIcon={<HomeOutlined />}
            onClick={handleGoHome}
            sx={{
              borderColor: '#CBD5E1',
              color: '#475569',
              '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Dashboard
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}

export default ErrorBoundary;
